import type { FC, PropsWithChildren } from 'react'

import React from 'react'

import styled from '@emotion/styled'

import { useTheme } from '@emotion/react'

import Divider from '@segment/matcha/components/Divider'

import Image from '@segment/matcha/components/Image'

import * as CopyText from '@segment/matcha/recipes/CopyText'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { items } from './data'
import type { Image as ImageProps } from './data'

const copy = {
  overline: `Key Findings`,
}

const KeyFindings: FC<PropsWithChildren<unknown>> = () => {
  const theme = useTheme()

  return (
    <Wrapper id="keyFindings">
      <StyledSectionBase variant="noCopy">
        <Section.Contents>
          <StyledCopyTextOverline tag="h2" variant="overline">
            {copy.overline}
          </StyledCopyTextOverline>
          <StyledDivider />
          {items.map(({ key, ...itemProps }) => (
            <CopyTextWrapper key={key}>
              <CopyText.Heading>{itemProps.heading}</CopyText.Heading>
              {itemProps.description.map((description, descIndex) => (
                <CopyText.Description variant="pSmall" key={`${descIndex}-${key}`}>
                  {description}
                </CopyText.Description>
              ))}
              <ImageWrapper imagesNumber={itemProps.images.length}>
                {itemProps.images?.map(({ src, webp, webp2x, mobile, height, width, alt }: ImageProps, index) => (
                  <picture key={index}>
                    {mobile && (
                      <>
                        <source
                          media={theme.breakpoints.down(`sm`, false)}
                          srcSet={`${mobile.src}, ${mobile.src2x} 2x`}
                        />
                        <source
                          media={theme.breakpoints.down(`sm`, false)}
                          type="image/webp"
                          srcSet={`${mobile.webp}, ${mobile.webp2x} 2x`}
                        />
                      </>
                    )}

                    <source type="image/webp" srcSet={`${webp}, ${webp2x} 2x`} />
                    <StyledImage width={width} height={height} src={src} alt={alt || `Illustration`} loading="lazy" />
                  </picture>
                ))}
              </ImageWrapper>
            </CopyTextWrapper>
          ))}
        </Section.Contents>
      </StyledSectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledCopyTextOverline = styled(CopyText.Overline)`
  text-transform: none;
  letter-spacing: 0;
`

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    padding: 0;
    padding-bottom: 24px;
  }
`

const CopyTextWrapper = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`

const StyledDivider = styled(Divider)`
  margin: 25px 0 0;
`

interface ImageWrapperProps {
  imagesNumber: number
}

const ImageWrapper = styled.div<ImageWrapperProps>`
  padding-top: ${({ imagesNumber }) => (imagesNumber > 0 ? `32px` : `0`)};
  display: grid;
  grid-template-rows: ${({ imagesNumber }) => `1fr `.repeat(imagesNumber)};
  row-gap: 48px;
`

const StyledImage = styled(Image)`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    max-width: 320px;
  }
`

export default KeyFindings
